/**
 * WonPilesRail — vertical strip flanking the felt that holds a team's
 * won-hand pile placeholder. Phase 8 (v1.2 visual revamp) layout slot only;
 * Phase 9 fills the pile tiles with card-back art and Phase 10 animates
 * card flight into the rail on each hand-win sweep.
 *
 * Reads:
 *   - view.tricksWon[team] — number of hands won this round
 *   - view.bidding.contractTeam — for the gold-accent contract highlight
 *
 * The rail is desktop-only (hidden at < 1024px in CSS); mobile relies on
 * the HUD's hands-won pills. The JSX renders at every viewport so snapshot
 * tests are stable.
 *
 * Props are fully derived from `team`/`side`; the component never receives
 * the redacted view directly (Phase 3 component-contract rule: components
 * read from store hooks, not props).
 */
import { useLobby, useView } from "../state/hooks.js";

export interface WonPilesRailProps {
  team: "A" | "B";
  side: "left" | "right";
}

function teamLabel(team: "A" | "B"): string {
  return `Team ${team}`;
}

/**
 * Look up the partner pair on this team given the local human's seat.
 * Used only for the rail's aria-label so screen readers say "Team A
 * (you and Carol)" rather than just "Team A".
 */
function teammates(
  team: "A" | "B",
  you: number | null,
  n: 4 | 6,
  seatNames: Map<number, string>,
): string[] {
  const names: string[] = [];
  for (let i = 0; i < n; i++) {
    const isA = i % 2 === 0;
    if ((team === "A") === isA) {
      const name = seatNames.get(i) ?? `seat ${i}`;
      names.push(you !== null && i === you ? `you (${name})` : name);
    }
  }
  return names;
}

export function WonPilesRail({ team, side }: WonPilesRailProps): JSX.Element | null {
  const view = useView();
  const lobby = useLobby();
  if (!view) return null;

  const count = view.tricksWon[team] ?? 0;
  const T = view.rc.totalTricks;
  const isContract = view.bidding.contractTeam === team;
  const n = view.config.players;

  const seatNames = new Map<number, string>();
  for (const s of lobby?.seats ?? []) {
    seatNames.set(s.seatIndex, s.displayName);
  }
  const partners = teammates(team, view.you ?? null, n, seatNames);
  const partnersLabel = partners.length > 0 ? ` (${partners.join(" and ")})` : "";

  // Render `count` filled tiles and (T - count) empty slots so the rail
  // reads as a progress strip. Capped at T tiles total. Phase-9: filled
  // tiles paint the `card-back` sprite from /cards.svg; empty tiles stay
  // as dashed placeholders.
  const tiles: JSX.Element[] = [];
  for (let i = 0; i < T; i++) {
    const filled = i < count;
    tiles.push(
      <span
        key={i}
        className={
          "won-pile-tile" + (filled ? " won-pile-tile-filled" : " won-pile-tile-empty")
        }
        aria-hidden="true"
      >
        {filled ? (
          <svg
            className="won-pile-tile-face"
            viewBox="0 0 100 140"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <use href="/cards.svg#card-back" />
          </svg>
        ) : null}
      </span>,
    );
  }

  return (
    <aside
      className={
        "won-pile-rail" +
        ` won-pile-rail-${side}` +
        ` won-pile-rail-team-${team}` +
        (isContract ? " won-pile-rail-contract" : "")
      }
      role="region"
      aria-label={`${teamLabel(team)}${partnersLabel}, ${count} of ${T} hands won`}
      data-team={team}
      data-side={side}
      data-count={count}
    >
      <header className="won-pile-rail-header">
        <span className="won-pile-rail-team">{teamLabel(team)}</span>
        <span className="won-pile-rail-count">
          <strong>{count}</strong>
          <span className="won-pile-rail-of"> / {T}</span>
        </span>
      </header>
      <div className="won-pile-rail-stack">{tiles}</div>
    </aside>
  );
}
